import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { isNull, uniqueId } from 'lodash'

import Modal from '@ublique/components/LoadingModal'
import { InlineLoading } from '@ublique/components/Loading'

import RadioForm from './RadioForm'
import { FiltersActions } from 'src/App/actions'
import { createFilterbarApiActions, getListAndFavorite } from '../actions'
import { addNotification } from 'components/Notifications/actions'

import '../index.scss'

const ModalFilterList = ({ open, close, data }) => {
  const { outputName, instanceId, filterNameRendered } = data

  const [filters, setFilters] = useState(null)
  const [favorite, setFavorite] = useState(null)
  const [selectedValue, setSelectedValue] = useState(filterNameRendered)

  const { t } = useTranslation('Common')
  const { deleteFilter, updateFilter, getDefaultFilter } =
    createFilterbarApiActions(outputName, instanceId)

  const onDeleteFilter = useCallback(
    async ({ id, name }) => {
      setFilters(null)
      await deleteFilter(id)

      addNotification({
        id: uniqueId('notif-'),
        title: `${t('MODAL_FILTER_DELETED')}: ${name}`,
        type: 'inline',
        kind: 'success'
      })

      const { filterList, favoriteFilter } = await getListAndFavorite(
        outputName
      )

      setFavorite(favoriteFilter ?? null)
      setFilters(filterList)
    },
    [outputName, t]
  )

  const onChangeFavorite = useCallback(
    async (newFavorite) => {
      setFilters(null)

      newFavorite.favorite = !newFavorite.favorite
      await updateFilter(newFavorite.id, newFavorite)

      // // Set the new favorite in the frontend
      const { filterList, favoriteFilter } = await getListAndFavorite(
        outputName
      )

      setFavorite(favoriteFilter ?? null)
      setFilters(filterList)
    },
    [favorite, updateFilter]
  )

  const onApplyFilter = useCallback(async () => {
    let filterName = 'None'

    if (selectedValue === 0 || selectedValue === 'None') {
      const { sections } = await getDefaultFilter()
      FiltersActions.update((prevState) => ({
        ...prevState,
        sections,
        filterName,
        keepStateOnSectionChange: false
      }))
    } else {
      const { sections, name } = filters.find(
        (item) => item.id === selectedValue || item.name === selectedValue
      )

      filterName = name

      FiltersActions.update((prevState) => ({
        ...prevState,
        sections,
        filterName,
        keepStateOnSectionChange: false
      }))
    }

    addNotification({
      id: uniqueId('notif-'),
      title: `${t('FILTER_LOADING')}: ${t(filterName)}`,
      type: 'inline',
      kind: 'success'
    })
    close()
  }, [t, filters, selectedValue])

  useEffect(() => {
    getListAndFavorite(outputName)
      .then(({ filterList, favoriteFilter }) => {
        setFavorite(favoriteFilter ?? null)
        setFilters(filterList)
      })
      .catch(() => setFilters([]))
  }, [])

  const renderFilters = useMemo(() => {
    if (isNull(filters)) return <InlineLoading description={t('Load')} />

    if (filters.length === 0) {
      return <p>{t('MODAL_FILTER_LIST_EMPTY')}</p>
    }

    return (
      <RadioForm
        t={t}
        filterList={filters}
        favorite={favorite}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        onChangeFavorite={onChangeFavorite}
        onDeleteFilter={onDeleteFilter}
      />
    )
  }, [t, filters, selectedValue, favorite])

  return (
    <Modal
      open={open}
      className='filter-modal'
      iconDescription={t('close')}
      modalHeading={t('MY_FILTERS')}
      onRequestClose={close}
      onRequestSubmit={onApplyFilter}
      onSecondarySubmit={close}
      primaryButtonText={t('Apply')}
      confirmLoadingMessage={t('Apply')}
      secondaryButtonText={t('CLOSE')}
      primaryButtonDisabled={isNull(filters) || filters.length === 0}
      preventCloseOnClickOutside
    >
      {renderFilters}
    </Modal>
  )
}

export default React.memo(ModalFilterList)
